// [CCTOWER-2441] 供应商报价模板配置 — 类型定义

import { t } from '@/i18n'

type TranslateFn = (key: string, values?: Record<string, unknown>) => string

/** 业务类型（单选） */
export type QuotationBusinessType =
  | 'PREPORT'    // 港前
  | 'LASTMILES'  // 尾程
  | 'SEA'        // 海运
  | 'AIR'        // 空运
  | 'RAIL'       // 铁路
  | 'EXPRESS'    // 快递

/**
 * 模板配置模块（多选，写入 templateTypes）
 * 含历史目录 groupType，便于预览/字段兼容
 */
export type QuotationTemplateType =
  | QuotationBusinessType
  | 'FCL'
  | 'LCL'
  | 'AIR_FCL'
  | 'AIR_LCL'

/** 模板状态：1-启用，0-禁用 */
export type TemplateStatus = 0 | 1

/** 模板状态常量 */
export const TEMPLATE_STATUS = {
  DISABLED: 0 as TemplateStatus,
  ENABLED: 1 as TemplateStatus
}

/** 字段类型（供应商H5渲染用） */
export type FieldType = 'text' | 'select' | 'date' | 'number' | 'fee' | 'fee-section' | 'field-group' | 'date-range' | 'suggest-multi' | 'weekdays' | 'suggest' | 'checkbox'

/** 费用表/字段组子字段（sub_fields JSON） */
export interface FeeSectionSubField {
  fieldName: string
  fieldType: string
  /** 是否只读：1-只读，0-可编辑 */
  readonly?: number
}

/** 字段配置项 */
export interface TemplateFieldConfig {
  fieldCode: string
  fieldName: string
  fieldType: FieldType
  required: boolean
  visible: boolean
  locked: boolean
  sortOrder: number
  options?: string[]
  groupType?: QuotationTemplateType
  subFields?: FeeSectionSubField[]
  requiredFeeIds?: string[]
}

/** 报价模板 */
export interface QuotationTemplate {
  id: string
  templateNo: string
  templateName: string
  /** 业务类型（单选） */
  businessType?: QuotationBusinessType | string
  /** 模板配置模块 */
  templateTypes: QuotationTemplateType[]
  status: TemplateStatus
  fields: TemplateFieldConfig[]
  fieldCount?: number
  supplierCount: number
  supplierNames?: string[]
  isStandard?: boolean
  updatedAt: string
  createdAt: string
}

/** 供应商关联 */
export interface SupplierAssociation {
  id: string
  supplierId: string
  supplierName: string
  templateId: string
  templateType: QuotationTemplateType | string
}

/** 供应商报价记录 */
export interface SupplierQuotation {
  id: string
  quotationNo: string
  supplierId: string
  supplierName: string
  templateId: string
  templateType: QuotationTemplateType | string
  templateName: string
  status: 'submitted' | 'abandoned'
  fieldData: Record<string, any>
  submittedAt: string
  createdAt: string
}

const BUSINESS_TYPE_VALUES: QuotationBusinessType[] = [
  'PREPORT', 'LASTMILES', 'SEA', 'AIR', 'RAIL', 'EXPRESS'
]

/** 业务类型 → 可选模板配置模块 */
export const BUSINESS_TYPE_MODULES: Record<QuotationBusinessType, QuotationTemplateType[]> = {
  PREPORT: ['PREPORT'],
  LASTMILES: ['LASTMILES'],
  SEA: ['PREPORT', 'LASTMILES', 'SEA'],
  AIR: ['PREPORT', 'LASTMILES', 'AIR'],
  RAIL: ['PREPORT', 'LASTMILES', 'RAIL'],
  EXPRESS: ['EXPRESS']
}

/** 模板类型/模块标签颜色 */
export const TEMPLATE_TYPE_COLOR: Record<string, string> = {
  PREPORT: 'blue',
  LASTMILES: 'purple',
  SEA: 'green',
  AIR: 'red',
  RAIL: 'orangered',
  EXPRESS: 'cyan',
  FCL: 'green',
  LCL: 'orange',
  AIR_FCL: 'red',
  AIR_LCL: 'cyan'
}

/** 显示顺序 */
export const TYPE_DISPLAY_ORDER: Record<string, number> = {
  PREPORT: 100,
  SEA: 200,
  FCL: 200,
  LCL: 210,
  AIR: 300,
  AIR_FCL: 300,
  AIR_LCL: 310,
  RAIL: 350,
  LASTMILES: 400,
  EXPRESS: 500
}

/** 获取类型/模块 i18n 标签 */
export function getTemplateTypeLabel(
  type: QuotationTemplateType | string,
  tr: TranslateFn = t
): string {
  const key = `supplierQuotationConfig.templateTypes.${type}`
  const label = tr(key)
  return label !== key ? label : String(type)
}

/** 业务类型下拉/单选选项 */
export function getBusinessTypeOptions(
  tr: TranslateFn = t
): { label: string; value: QuotationBusinessType; color: string }[] {
  return BUSINESS_TYPE_VALUES.map(value => ({
    value,
    label: getTemplateTypeLabel(value, tr),
    color: TEMPLATE_TYPE_COLOR[value] || ''
  }))
}

/** 按业务类型取可选模板配置模块选项 */
export function getModuleOptionsByBusinessType(
  businessType: QuotationBusinessType | string | undefined,
  tr: TranslateFn = t
): { label: string; value: QuotationTemplateType; color: string }[] {
  if (!businessType || !(businessType in BUSINESS_TYPE_MODULES)) {
    return []
  }
  return BUSINESS_TYPE_MODULES[businessType as QuotationBusinessType].map(value => ({
    value,
    label: getTemplateTypeLabel(value, tr),
    color: TEMPLATE_TYPE_COLOR[value] || ''
  }))
}

/** @deprecated 兼容旧调用：业务类型选项 */
export function getTemplateTypeOptions(
  tr: TranslateFn = t
): { label: string; value: QuotationTemplateType; color: string }[] {
  return getBusinessTypeOptions(tr)
}

/** @deprecated */
export const TEMPLATE_TYPE_OPTIONS: { label: string; value: QuotationTemplateType; color: string }[] = []

/** @deprecated */
export const TEMPLATE_TYPE_LABEL = {} as Record<QuotationTemplateType, string>

/** 公共字段（跨类型共享） */
export const SHARED_FIELD_CODES: string[] = ['validUntil']
